import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { isPlatformStaff } from '../lib/roleHelpers'
import db from '../lib/db'
import useOnlineStatus from '../lib/useOnlineStatus'
import { Shield, Target, QrCode as QrCodeIcon, LogOut, Crosshair, X, Plus, Building2, Menu } from 'lucide-react'
import { QRCode } from 'react-qr-code'
import QRScanner from '../components/QRScanner'
import ViewSwitcher from '../components/ViewSwitcher'
import NotificationBell from '../components/NotificationBell'
import WhatsNew from '../components/WhatsNew'
import BleedOutTimer from '../components/BleedOutTimer'
import SyncIndicator from '../components/SyncIndicator'
import setThemeColor from '../lib/themeColor'
import { downloadQRPng } from '../lib/printQR'
import useOrgSettings from '../lib/useOrgSettings'
import { subscribeForegroundNotifications } from '../lib/foregroundNotify'
import cache from '../lib/localCache'
import OfflineGuard from '../components/OfflineGuard'
import EventsTab from '../components/EventsTab'
import IdentityCard from '../components/IdentityCard'
import DevScanPanel from '../components/DevScanPanel'
import FragoPhaseStepper from '../components/FragoPhaseStepper'
import { clearAllUserState } from '../lib/authCleanup'
import realtimeManager from '../lib/realtimeManager'
import DataState from '../components/DataState'
import CountdownTimer from '../components/CountdownTimer'
import RefereeView from '../components/RefereeView'
import useEffectiveRole from '../lib/useEffectiveRole'
import WarCrimeReportButton from '../components/WarCrimeReportButton'
import BlindManOverlay from '../components/BlindManOverlay'

// Recon-specific inline styles (can't do pseudo-elements or backdrop-filter in pure CSS vars)
const S = {
  page: {
    minHeight: '100vh',
    background: 'radial-gradient(ellipse at 20% 10%, rgba(40,70,50,.6), transparent 60%), radial-gradient(ellipse at 80% 90%, rgba(60,80,50,.4), transparent 60%), #14181a',
    color: '#e0e8de',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  panel: {
    background: 'rgba(28, 38, 38, .6)',
    border: '1px solid rgba(140, 180, 130, .2)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
  },
  nav: {
    background: 'rgba(28, 38, 38, .8)',
    borderBottom: '1px solid rgba(140, 180, 130, .2)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    padding: '10px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif",
    fontSize: '15px',
    fontWeight: 700,
    color: '#9adb6e',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },
  displayFont: {
    fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif",
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },
  monoFont: {
    fontFamily: "'JetBrains Mono', monospace",
  },
  accent: { color: '#9adb6e' },
  cyan: { color: '#4dd6c2' },
  warn: { color: '#ff6a3d' },
  text: { color: '#e0e8de' },
  muted: { color: '#8aa090' },
  faded: { color: '#5a6a60' },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '18px',
    padding: '0 8px',
    fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif",
    fontSize: '10px',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    border: '1px solid',
    background: 'rgba(154, 219, 110, 0.08)',
  },
  btnScan: {
    flex: 1,
    padding: '18px 12px',
    background: '#4dd6c2',
    color: '#14181a',
    fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif",
    fontSize: '16px',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    border: 'none',
    borderRadius: '0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    boxShadow: '0 0 8px rgba(77, 214, 194, .3)',
  },
  btnDown: {
    flex: 1,
    padding: '18px 12px',
    background: '#ff6a3d',
    color: '#14181a',
    fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif",
    fontSize: '16px',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    border: 'none',
    borderRadius: '0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  btnPrimary: {
    fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif",
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    border: 'none',
    cursor: 'pointer',
    background: '#9adb6e',
    color: '#14181a',
    boxShadow: '0 0 8px rgba(154, 219, 110, .5)',
  },
  btnSecondary: {
    fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif",
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    border: '1px solid rgba(140, 180, 130, .2)',
    cursor: 'pointer',
    background: 'rgba(28, 38, 38, .6)',
    color: '#8aa090',
  },
}

// Type-edge colors for bounty cards
const typeEdge = {
  kill: '#ff6a3d',
  escort: '#4dd6c2',
  defend: '#9adb6e',
}
const typeTagStyle = {
  kill: { color: '#ff6a3d', borderColor: 'rgba(255, 106, 61, .4)', background: 'rgba(255, 106, 61, .08)' },
  escort: { color: '#4dd6c2', borderColor: 'rgba(77, 214, 194, .4)', background: 'rgba(77, 214, 194, .08)' },
  defend: { color: '#9adb6e', borderColor: 'rgba(154, 219, 110, .4)', background: 'rgba(154, 219, 110, .08)' },
}

export default function PlayerView() {
  const { user, profile } = useAuth()
  const isOnline = useOnlineStatus()
  const [localProfile, setLocalProfile] = useState(null)
  const [activeBounties, setActiveBounties] = useState([])
  const [myBounties, setMyBounties] = useState([])
  const [stats, setStats] = useState({})
  const [showQR, setShowQR] = useState(false)
  // ANU-83 — mobile-only overflow menu for admin / org-admin / sync. Top
  // row stays scannable on small phones with logo + bell + scan + avatar.
  const [navMenuOpen, setNavMenuOpen] = useState(false)
  const [pursuits, setPursuits] = useState([])
  const [userFactionId, setUserFactionId] = useState(null)
  const [activeTab, setActiveTab] = useState('ops')
  const [showBleedOut, setShowBleedOut] = useState(false)
  const [showScanner, setShowScanner] = useState(false)
  const [myTeams, setMyTeams] = useState([])
  const [teamsCollapsed, setTeamsCollapsed] = useState(() =>
    localStorage.getItem('dc_teams_collapsed') === 'true'
  )
  const [pendingConfirmations, setPendingConfirmations] = useState([])
  // TD-17: kills *I* scanned that are still awaiting honor/dishonor. Surfaces
  // on the scorecard so a hunter sees their scan registered even before the
  // target presses confirm.
  const [myPendingKills, setMyPendingKills] = useState(0)
  const [honorsCollapsed, setHonorsCollapsed] = useState(() => {
    return localStorage.getItem('dc_honors_collapsed') === 'true'
  })
  // FIX 5 — collapsed state for the MISSIONS "COMPLETED / FAILED"
  // archive. Defaults to collapsed so the active list is the lead.
  // Empty/missing key counts as collapsed (true) — same convention
  // as the Dashboard archive sections.
  const [resolvedMissionsCollapsed, setResolvedMissionsCollapsed] = useState(() =>
    localStorage.getItem('dc_resolved_missions_collapsed') !== 'false'
  )
  const toggleResolvedMissions = () => setResolvedMissionsCollapsed(prev => {
    const next = !prev
    localStorage.setItem('dc_resolved_missions_collapsed', String(next))
    return next
  })
  // activeEventId tracks the event the player has selected. Drives the
  // realtime-subscription filter (so we only refetch bounties when the
  // user's event changes, not on every system-wide bounty change) and
  // gates the scanner — kills must never land with null event_id.
  const [activeEventId, setActiveEventId] = useState(() =>
    localStorage.getItem('dc_active_event')
  )
  const [activeEventStatus, setActiveEventStatus] = useState(null)

  // ANU-103 — PROTECT bounty state
  const [myProtectBounty, setMyProtectBounty] = useState(null)   // bounty where I'm VIP
  const [myProtectorReg, setMyProtectorReg] = useState(null)     // my protector registration
  const [protectRegistrations, setProtectRegistrations] = useState([]) // for VIP card
  const [teamInPlaceBusy, setTeamInPlaceBusy] = useState(false)
  const [deadConfirm, setDeadConfirm] = useState(false)
  const [deadCountdown, setDeadCountdown] = useState(30)
  const [deadBusy, setDeadBusy] = useState(false)
  const [abandonBusy, setAbandonBusy] = useState(false)

  // Bug 31: if the user's stored active-event has closed/cancelled since
  // last visit, drop the pointer so the OPS tab clears (no stale faction
  // scores, no IdentityCard from a finished event). Live and endex stay
  // sticky — endex still has post-game data to show.
  useEffect(() => {
    if (!activeEventId) { setActiveEventStatus(null); return }
    let cancelled = false
    ;(async () => {
      const { data } = await supabase
        .from('events')
        .select('status').eq('id', activeEventId).limit(1)
      const status = data?.[0]?.status
      if (cancelled) return
      setActiveEventStatus(status || null)
      if (!status || status === 'closed' || status === 'cancelled') {
        localStorage.removeItem('dc_active_event')
        setActiveEventId(null)
      }
    })()
    return () => { cancelled = true }
  }, [activeEventId])
  // Org-level fog-of-war settings for the active event. Players never see
  // the GM bypass, so this hook always returns the player-facing view.
  const { hideFactionScores } = useOrgSettings(activeEventId)
  const [eventGateError, setEventGateError] = useState(null)
  const [myFragos, setMyFragos] = useState([])
  // Track 5.3 — loading + error state for the three player-facing tabs.
  // Each fetcher wraps its body in try/catch and sets the matching
  // error setter so DataState can surface a retry button instead of
  // pretending the list is empty.
  const [fragosLoading, setFragosLoading] = useState(true)
  const [fragosError, setFragosError] = useState(null)
  const [bountiesLoading, setBountiesLoading] = useState(true)
  const [bountiesError, setBountiesError] = useState(null)
  const [teamsLoading, setTeamsLoading] = useState(true)
  const [teamsError, setTeamsError] = useState(null)
  const [myChildNodes, setMyChildNodes] = useState([])  // children of nodes I lead — for DELEGATE picker
  // Active pause for the currently selected event — drives the GAME PAUSED
  // banner and the scanner lockout.
  const [activePause, setActivePause] = useState(null)
  // Dev-only synthetic scan text. When set, QRScanner skips the camera and
  // runs the normal handleClaim against this string. Set by DevScanPanel.
  const [simulatedQrText, setSimulatedQrText] = useState(null)
  // Org-admin gate for the nav entry to /org. Mirrors Dashboard's check —
  // m55_admin always sees it; everyone else must be in org_members as
  // owner/admin.
  const [isOrgAdmin, setIsOrgAdmin] = useState(false)

  // In PlayerView (Recon)
  useEffect(() => { setThemeColor('recon') }, [])

  useEffect(() => {
    if (!user?.id) return
    if (isPlatformStaff(profile)) { setIsOrgAdmin(true); return }
    let cancelled = false
    db.getIsOrgAdmin(user.id).then((v) => { if (!cancelled) setIsOrgAdmin(!!v) })
    return () => { cancelled = true }
  }, [user?.id, profile?.role])

  useEffect(() => {
    fetchProfile()
    fetchBounties()
    fetchEventStats()
    fetchStats()
    fetchUserFaction()
    fetchPendingConfirmations()
    fetchMyTeams()
    fetchEventFactions()
    fetchMyFragos()
    fetchProtectData()
    // Pursuits are now fetched on mount + after user actions only — no
    // longer chained inside fetchBounties (which polled every 15s and
    // dragged a bounty_pursuits round-trip with each cycle).
    fetchPursuits()
  }, [])

  // ANU-14 — auto-select on login/page-load. Until now the auto-select
  // only fired inside EventsTab's render path, so a player landing on
  // OPS with no stored event saw an empty bounties list and had to
  // click into EVENTS first. This effect runs once per mount and only
  // when no event is already pinned in localStorage (player's explicit
  // choice always wins). Single active registered → snap into it.
  // Multiple → drop them on the EVENTS tab so they pick. None → leave
  // OPS empty for the existing "no bounties" copy to show.
  useEffect(() => {
    if (!user?.id) return
    if (localStorage.getItem('dc_active_event')) return
    let cancelled = false
    ;(async () => {
      const registered = await db.getRegisteredEventsForUser(user.id)
      if (cancelled) return
      const active = (registered || []).filter(e => e.status === 'live' || e.status === 'active')
      if (active.length === 1) {
        const id = active[0].id
        localStorage.setItem('dc_active_event', id)
        window.dispatchEvent(new CustomEvent('dc_event_selected', { detail: { eventId: id } }))
      } else if (active.length > 1) {
        setActiveTab('events')
      }
      // 0 active → fall through; EVENTS tab + OPS empty states cover it.
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  // Realtime subscription is keyed on activeEventId so the filter targets
  // only the user's event. Re-runs on event change to subscribe to the
  // new event. With no event selected, no subscription (and no realtime
  // cascades into fetchBounties).
  useEffect(() => {
    if (!activeEventId) { setActivePause(null); return }
    fetchActivePause()
    const pauseInterval = setInterval(fetchActivePause, 30000)
    const cleanup = subscribeToChanges(activeEventId)
    // Foreground local-notification stream — fires the same Web Push
    // categories via showNotification() while the tab is open. The Edge
    // Function-driven pushes only land when the app is backgrounded.
    const stopFg = subscribeForegroundNotifications({ user, activeEventId })
    // Track 1.3 — events UPDATE listener via the central realtime
    // manager. Covers a spec'd gap (status flips were only picked up on
    // remount before). Proof-of-pattern for future channel migrations.
    realtimeManager.setActiveEvent(activeEventId)
    const stopEventStatus = realtimeManager.subscribe(
      'player-event-status',
      'events',
      async () => {
        if (!navigator.onLine) return
        await db.bustCache('events')
        // The Bug 31 effect already re-resolves the stored event's
        // status on activeEventId change; reuse that path by toggling
        // the stored id without changing it.
        const v = localStorage.getItem('dc_active_event')
        if (v) {
          localStorage.setItem('dc_active_event', v)
          window.dispatchEvent(new CustomEvent('dc_event_selected', { detail: { eventId: v } }))
        }
      },
      { event: 'UPDATE', filter: `id=eq.${activeEventId}` }
    )
    return () => {
      clearInterval(pauseInterval)
      cleanup && cleanup()
      stopFg()
      stopEventStatus()
    }
  }, [activeEventId])

  const fetchActivePause = async () => {
    if (!activeEventId) { setActivePause(null); return }
    const { data } = await supabase.from('event_pauses')
      .select('*').eq('event_id', activeEventId)
      .is('actual_end', null).not('actual_start', 'is', null)
      .order('actual_start', { ascending: false }).limit(1)
    setActivePause(data?.[0] || null)
  }

  useEffect(() => {
    if (isOnline) {
      fetchStats()
      fetchPendingConfirmations()
      fetchMyTeams()
    }
  }, [isOnline])

  useEffect(() => {
    // Polling stretched from 15s → 60s. Realtime sub covers fast-changing
    // bounty events; this is the safety net for things realtime might miss
    // (network blip, missed event, expiry). 60s is enough.
    const interval = setInterval(async () => {
      if (!navigator.onLine) return
      // Bug 37: bust SWR caches each polling tick so the safety-net poll
      // actually fetches fresh data instead of re-serving the previous
      // cached snapshot.
      await db.bustCache('bounties', 'kill_log')
      fetchBounties()
      fetchPendingConfirmations()
    }, 60000)
    return () => clearInterval(interval)
  }, [])

  // Single dc_event_selected listener. Updates activeEventId state (which
  // re-keys the realtime subscription), then refreshes ALL event-scoped
  // data — bounties, stats, factions (for visibility filtering), and FRAGOs.
  // Earlier this only refreshed bounties + event stats, so factions/FRAGOs
  // stayed pinned to whichever event the player loaded with (Bug 3).
  useEffect(() => {
    const handleEventChange = (e) => {
      const newId = e?.detail?.eventId || localStorage.getItem('dc_active_event')
      setActiveEventId(newId)
      fetchBounties()
      fetchEventStats()
      fetchEventFactions()
      fetchMyFragos()
    }
    window.addEventListener('dc_event_selected', handleEventChange)
    return () => window.removeEventListener('dc_event_selected', handleEventChange)
  }, [])

  const [eventStats, setEventStats] = useState(null)

  const fetchProfile = async () => {
    const data = await db.getUserProfile(user.id)
    setLocalProfile(data)
  }

  const [eventPlayerFactions, setEventPlayerFactions] = useState({})

  const fetchEventFactions = async () => {
    const storedEventId = localStorage.getItem('dc_active_event')
    if (!storedEventId) return
    const { data } = await supabase
      .from('event_players')
      .select('user_id, faction_id')
      .eq('event_id', storedEventId)
    const map = {}
    ;(data || []).forEach(ep => { map[ep.user_id] = ep.faction_id })
    setEventPlayerFactions(map)
  }

  const fetchBounties = async () => {
    const storedEventId = localStorage.getItem('dc_active_event')
    // Bug 2: bounty banner must NOT show until the player has explicitly
    // selected an event (kills posted with a stale event would corrupt
    // scoreboards). The previous fallback to "any active event the player is
    // registered in" leaked bounties on first load before selection.
    if (!storedEventId) {
      setActiveBounties([])
      setMyBounties([])
      setBountiesLoading(false); setBountiesError(null)
      return
    }
    setBountiesLoading(true); setBountiesError(null)
    try {

    const myEvents = await db.getActiveEventsForUser(user.id)
    const registeredIds = (myEvents || []).map(e => e.id)
    // getActiveEventsForUser already filters to status in ('live','active');
    // accept either here so the dev 7-state lifecycle ('live') doesn't drop
    // the entire bounty board.
    const activeEvent = registeredIds.includes(storedEventId)
      ? myEvents.find(e => e.id === storedEventId && (e.status === 'live' || e.status === 'active'))
      : null

    if (!activeEvent) {
      setActiveBounties([])
      setMyBounties([])
      return
    }

    // Fetch faction map for this event
    const { data: factionData } = await supabase
      .from('event_players')
      .select('user_id, faction_id')
      .eq('event_id', activeEvent.id)
    const factionMap = {}
    ;(factionData || []).forEach(ep => { factionMap[ep.user_id] = ep.faction_id })

    const active = await db.getActiveBountiesForEvent(activeEvent.id, user.id, userFactionId)

    // Get current user's faction from the same map
    const myFaction = factionMap[user.id]

    // Filter by faction visibility
    const filtered = (active || []).filter(bounty => {
      if (!myFaction) return true
      const targetFaction = factionMap[bounty.target_id]
      if (!targetFaction) return true
      if (bounty.bounty_type === 'defend') return targetFaction === myFaction
      if (bounty.bounty_type === 'kill') return targetFaction !== myFaction
      return true
    })

    setActiveBounties(filtered)
    // Pursuits no longer auto-fetched here — that chained a bounty_pursuits
    // round-trip onto every fetchBounties (polled every 60s + every realtime
    // event), which compounded into the request storm. Pursuits are
    // refreshed on mount and after commit/standDown click handlers.

    const mine = await db.getMyBounties(user.id)
    setMyBounties(activeEvent ? (mine || []).filter(b => b.event_id === activeEvent.id) : [])
    } catch (err) {
      console.warn('[PlayerView] fetchBounties failed', err)
      setBountiesError(err)
    } finally {
      setBountiesLoading(false)
    }
  }

  // ANU-103 — fetch PROTECT bounty data (VIP role + protector role)
  const fetchProtectData = async () => {
    if (!user?.id || !activeEventId || !navigator.onLine) return
    // Am I a VIP in an active protect bounty?
    const { data: vipBounty } = await supabase
      .from('bounties')
      .select('id, protect_mode, min_protectors, active_duration, activated_at, destination_cp_id, bonus_time_minutes, bonus_points, status, notes, issuer:users!bounties_issued_by_fkey(callsign, display_name)')
      .eq('vip_player_id', user.id)
      .eq('event_id', activeEventId)
      .eq('bounty_type', 'protect')
      .in('status', ['registration', 'active'])
      .limit(1)
      .maybeSingle()
    setMyProtectBounty(vipBounty || null)

    if (vipBounty) {
      const { data: regs } = await supabase
        .from('protect_registrations')
        .select('id, protector_id, registered_at, abandoned_at, protector:users!protect_registrations_protector_id_fkey(callsign, display_name)')
        .eq('bounty_id', vipBounty.id)
      setProtectRegistrations(regs || [])
    } else {
      setProtectRegistrations([])
    }

    // Am I a protector on someone else's active protect bounty?
    const { data: myReg } = await supabase
      .from('protect_registrations')
      .select('id, bounty_id, registered_at, abandoned_at, bounty:bounties!protect_registrations_bounty_id_fkey(id, protect_mode, status, active_duration, activated_at, notes, vip:users!bounties_vip_player_id_fkey(callsign, display_name))')
      .eq('protector_id', user.id)
      .is('abandoned_at', null)
      .limit(1)
      .maybeSingle()
    setMyProtectorReg(myReg && myReg.bounty?.status && ['registration', 'active'].includes(myReg.bounty.status) ? myReg : null)
  }

  const fetchStats = async () => {
    if (!navigator.onLine) { setStats(null); return }
    // Career stats now come from v_player_career_stats. Falls back to the
    // legacy player_stats row if the view query returns nothing (e.g. brand
    // new account) so the bar still renders zeros instead of "offline".
    const data = await db.getCareerStats(user.id)
    if (data) { setStats(data); return }
    const legacy = await db.getPlayerStats(user.id)
    setStats(legacy || {})
  }

  const fetchEventStats = async () => {
    const storedEventId = localStorage.getItem('dc_active_event')
    // No event selected: bar renders zeros under EVENT STATS instead of
    // disappearing — the user toggling between event and career still sees
    // a sane response.
    if (!storedEventId) {
      setEventStats({
        total_points: 0, total_deaths: 0,
        career_confirmed_kills: 0, career_unconfirmed_kills: 0,
        bounties_claimed: 0, bounties_survived: 0,
        is_event_stats: true,
      })
      return
    }
    const data = await db.getEventStats(user.id, storedEventId)
    setEventStats(data)
  }

  const fetchPendingConfirmations = async () => {
    const data = await db.getPendingHonors(user.id)
    setPendingConfirmations(data || [])
    // TD-17: while we're here, count kills *I* submitted that are still
    // waiting for the target to confirm/dishonor. Cheap second query —
    // same realtime cadence as pendingConfirmations.
    const { count: pkCount } = await supabase
      .from('kill_log')
      .select('id', { count: 'exact', head: true })
      .eq('killer_id', user.id)
      .eq('status', 'pending')
    setMyPendingKills(pkCount ?? 0)
  }

  const fetchMyTeams = async () => {
    setTeamsLoading(true); setTeamsError(null)
    try {
      const data = await db.getMyTeams(user.id)
      setMyTeams(data || [])
    } catch (err) {
      console.warn('[PlayerView] fetchMyTeams failed', err)
      setTeamsError(err)
    } finally {
      setTeamsLoading(false)
    }
  }

  // FRAGOs visible to this player: broadcast to their faction OR assigned
  // to a node they're a member of OR lead. Also resolves the children of
  // any node they lead (for the DELEGATE button picker).
  const fetchMyFragos = async () => {
    const eventId = activeEventId || localStorage.getItem('dc_active_event')
    if (!eventId) { setMyFragos([]); setMyChildNodes([]); setFragosLoading(false); setFragosError(null); return }
    setFragosLoading(true); setFragosError(null)
    try {

    // Player's faction in this event
    const { data: epRows } = await supabase
      .from('event_players')
      .select('faction_id')
      .eq('user_id', user.id)
      .eq('event_id', eventId)
      .limit(1)
    const myFactionId = epRows?.[0]?.faction_id || null

    // Nodes I'm a member of OR lead (in this event). Pull every node in the
    // event up-front so memberships can be matched client-side — avoids the
    // mcs_nodes!inner join that previously hid platoon leaders from the
    // DELEGATE picker.
    const { data: eventNodes } = await supabase
      .from('mcs_nodes')
      .select('id, name, callsign, parent_id, faction_id, assigned_player_id')
      .eq('event_id', eventId)
    const eventNodeIds = (eventNodes || []).map(n => n.id)

    const [{ data: memberRows }, { data: leaderRows }] = await Promise.all([
      eventNodeIds.length
        ? supabase.from('mcs_node_members')
            .select('node_id').eq('user_id', user.id).in('node_id', eventNodeIds)
        : Promise.resolve({ data: [] }),
      supabase.from('mcs_nodes')
        .select('id').eq('event_id', eventId).eq('assigned_player_id', user.id),
    ])
    const myNodeIds = new Set([
      ...(memberRows || []).map(r => r.node_id),
      ...(leaderRows || []).map(n => n.id),
    ])
    const myLeaderNodeIds = new Set((leaderRows || []).map(n => n.id))

    // Children of any node I belong to (for DELEGATE-down).
    const kids = (eventNodes || []).filter(n => n.parent_id && myNodeIds.has(n.parent_id))
    setMyChildNodes(kids)

    // FIX 9 — for delegated-FRAGO visibility, build the set of nodes
    // that include me OR are my ancestors. A FRAGO delegated to node Y
    // is mine if Y is in my ancestor chain (I'm Y itself or one of
    // its descendants). Walk parent_id from each of my nodes up; cap
    // at 10 hops to defuse any cycle drift.
    const myAncestorNodeIds = new Set(myNodeIds)
    for (const nid of myNodeIds) {
      let cur = nid
      let safety = 10
      while (cur && safety--) {
        const node = (eventNodes || []).find(n => n.id === cur)
        if (!node?.parent_id) break
        myAncestorNodeIds.add(node.parent_id)
        cur = node.parent_id
      }
    }

    // FRAGOs in event with their assignments. Pull commander_route
    // so the MISSIONS filter can enforce the routing fix:
    //   broadcast → every faction-target member sees it
    //   any other route → only nodes with an assignment row see it
    const { data: fragoBounties } = await supabase
      .from('bounties')
      .select('*, frago_assignments(id, node_id, status, parent_assignment_id, node:mcs_nodes(id, name, callsign, faction_id, assigned_player_id, parent_id))')
      .eq('event_id', eventId)
      .eq('category', 'frago')
      .neq('status', 'queued')
      .order('issued_at', { ascending: false })

    // MISSIONS tab surfaces the *current ops* list (active + pursuing)
    // plus a collapsed "completed / failed" archive so resolved missions
    // don't linger in the active list (FIX 5 / FIX 10). Queued FRAGOs
    // are GM setup — still hidden from players. cancelled lands in the
    // resolved bucket too.
    const isLive = b => (
      b.status === 'active' ||
      b.status === 'claimed' ||
      b.status === 'failed' ||
      b.status === 'expired' ||
      b.status === 'cancelled' ||
      (b.frago_assignments || []).some(a => a.status === 'pursuing' && a.node && myNodeIds.has(a.node_id))
    )
    // Closest-ancestor lookup. From a starting node, walk up parent_id
    // and return the chain (closest first → root last). The visibility
    // rule below uses the FIRST assignment hit in that chain as the
    // controlling row so a declined Cadre branch doesn't keep seeing
    // the FRAGO via the upstream accepted Commander.
    const TERMINAL_STATUSES = new Set(['declined','aborted','completed','failed','reassigned'])
    const walkAncestors = (startNodeId) => {
      const chain = []
      let cur = startNodeId
      let safety = 12
      while (cur && safety-- > 0) {
        chain.push(cur)
        const node = (eventNodes || []).find(n => n.id === cur)
        if (!node?.parent_id) break
        cur = node.parent_id
      }
      return chain
    }

    const filtered = (fragoBounties || []).filter(b => {
      if (!isLive(b)) return false
      // Status-aware closest-ancestor visibility. Walk up parent_id
      // from each of my nodes; first assignment hit is the controlling
      // one. Then:
      //   • pending / active on MY OWN node   → visible (I'm being asked to accept)
      //   • pending / active on HIGHER node   → NOT visible (only that node's
      //                                          direct members see it until accept)
      //   • accepted on me or any ancestor    → visible (the chain is greenlit
      //                                          and cascading down)
      //   • delegated on me or any ancestor   → NOT visible (passed off to a
      //                                          specific child branch; I'm not it)
      //   • pursuing on my own node           → visible (legacy broadcast opt-in)
      //   • terminal (declined / aborted /
      //     completed / failed / reassigned)  → NOT visible
      const assigns = b.frago_assignments || []
      const fragoResolved = ['claimed','failed','expired','cancelled'].includes(b.status) || b.phase === 'resolved'
      if (assigns.length > 0) {
        // ANU-111: resolved FRAGOs (expired/failed/claimed/cancelled) have all their
        // assignments cascade-failed by expire_bounties(). The TERMINAL_STATUSES check
        // below would hide them from the player's archive. Bypass assignment-status
        // logic for resolved FRAGOs — any connection to the ancestor chain is sufficient
        // to show the FRAGO in the "Completed / Failed" bucket.
        if (fragoResolved) {
          const assignedNodeIds = new Set(assigns.map(a => a.node_id))
          return [...myAncestorNodeIds].some(nid => assignedNodeIds.has(nid))
        }
        const assignByNode = new Map(assigns.map(a => [a.node_id, a]))
        let visible = false
        for (const myNodeId of myNodeIds) {
          const chain = walkAncestors(myNodeId)
          let controlling = null
          let controllingIdx = -1
          for (let i = 0; i < chain.length; i++) {
            const a = assignByNode.get(chain[i])
            if (a) { controlling = a; controllingIdx = i; break }
          }
          if (!controlling) continue
          if (TERMINAL_STATUSES.has(controlling.status)) continue
          if (controlling.status === 'delegated') continue
          const onMyNode = controllingIdx === 0
          if (controlling.status === 'accepted') { visible = true; break }
          // pending / active / pursuing — only visible when on my own node
          if (onMyNode) { visible = true; break }
        }
        return visible
      }
      // Legacy fallback for pre-routing FRAGOs with zero assignments
      // (dev fixture data from before the auto-route cascade landed).
      if (b.target_type === 'faction'
          && (b.target_faction_id == null || b.target_faction_id === myFactionId)) {
        return true
      }
      return false
    }).map(b => ({
      ...b,
      // Tag the assignments visible to this player so card UI can act on them
      myAssignments: (b.frago_assignments || []).filter(a => myLeaderNodeIds.has(a.node_id) || myNodeIds.has(a.node_id)),
    }))
    setMyFragos(filtered)
    } catch (err) {
      // Track 5.3 — capture the error so the MISSIONS tab can surface
      // a retry button. The empty list is preserved so the rest of the
      // page renders normally; the DataState wrapper takes over the
      // missions section.
      console.warn('[PlayerView] fetchMyFragos failed', err)
      setFragosError(err)
    } finally {
      setFragosLoading(false)
    }
  }

  // Broadcast FRAGO pursuit. The player declares interest by inserting an
  // assignment row with status='pursuing' tied to their primary node. The GM
  // can later promote one of the pursuing nodes to a formal delegation
  // (frago_assignments → status='active') from the FRAGOS tab. Stand-down
  // simply deletes the player's pursuit row (RLS allows owner-delete).
  const pursueFrago = async (bountyId) => {
    // Resolve the player's primary node in this event — first leadership
    // role they hold, else first membership row. Without a node we can't
    // pursue (a pursuit MUST tie to a node).
    const eventId = activeEventId || localStorage.getItem('dc_active_event')
    if (!eventId) return
    const { data: leaderRows } = await supabase.from('mcs_nodes')
      .select('id').eq('event_id', eventId).eq('assigned_player_id', user.id).limit(1)
    let nodeId = leaderRows?.[0]?.id || null
    if (!nodeId) {
      const { data: memberRows } = await supabase.from('mcs_node_members')
        .select('node_id, mcs_nodes!inner(event_id)').eq('user_id', user.id).limit(20)
      const m = (memberRows || []).find(r => r.mcs_nodes?.event_id === eventId)
      nodeId = m?.node_id || null
    }
    if (!nodeId) {
      alert('You must be assigned to an MCS node before pursuing a FRAGO.')
      return
    }
    const { error } = await supabase.from('frago_assignments').insert({
      bounty_id: bountyId,
      node_id: nodeId,
      assigned_by: user.id,
      status: 'pursuing',
    })
    if (error) {
      alert('Pursue failed: ' + error.message)
      return
    }
    fetchMyFragos()
  }

  const standDownFrago = async (assignmentId) => {
    const { error } = await supabase.from('frago_assignments').delete().eq('id', assignmentId)
    if (error) {
      alert('Stand-down failed: ' + error.message)
      return
    }
    fetchMyFragos()
  }

  // SEC 2 — delegation flow via SECURITY DEFINER RPCs. RPCs handle
  // the parent-assignment chain (delegate flips parent → 'delegated';
  // decline/abort reopens parent so up-chain can re-delegate) and
  // gate caller permission server-side (node leader, member, or
  // GM-tier).
  const acceptFragoAssignment = async (assignmentId) => {
    const { data, error } = await supabase.rpc('frago_accept_assignment', { p_assignment_id: assignmentId })
    if (error || data?.ok === false) {
      alert('Accept failed: ' + (error?.message || data?.reason || 'unknown'))
      return
    }
    fetchMyFragos()
  }

  const declineFragoAssignment = async (assignmentId) => {
    if (!window.confirm('Decline this assignment? It returns to whoever delegated it.')) return
    const { data, error } = await supabase.rpc('frago_decline_assignment', { p_assignment_id: assignmentId })
    if (error || data?.ok === false) {
      alert('Decline failed: ' + (error?.message || data?.reason || 'unknown'))
      return
    }
    fetchMyFragos()
  }

  const abortFragoAssignment = async (assignmentId, parentNodeLabel) => {
    const msg = parentNodeLabel
      ? `Abort this mission? It will be returned to ${parentNodeLabel} for reassignment.`
      : 'Abort this mission? It will be returned for reassignment. Completed objectives are preserved.'
    if (!window.confirm(msg)) return
    const { data, error } = await supabase.rpc('frago_abort_assignment', { p_assignment_id: assignmentId })
    if (error || data?.ok === false) {
      alert('Abort failed: ' + (error?.message || data?.reason || 'unknown'))
      return
    }
    fetchMyFragos()
  }

  const delegateFragoToChild = async (parentAssignmentId, childNodeId) => {
    if (!childNodeId || !parentAssignmentId) return
    const { data, error } = await supabase.rpc('frago_delegate_assignment', {
      p_parent_assignment_id: parentAssignmentId,
      p_child_node_id: childNodeId,
    })
    if (error || data?.ok === false) {
      alert('Delegate failed: ' + (error?.message || data?.reason || 'unknown'))
      return
    }
    // mission_events insert lives inside the RPC now.
    fetchMyFragos()
  }


  const confirmKill = async (killId) => {
    await db.confirmKill({ killId })
    fetchPendingConfirmations()
  }

  const dismissKill = async (killId) => {
    await db.dismissKill({ killId })
    fetchPendingConfirmations()
  }

  const toggleHonorsCollapsed = () => {
    const next = !honorsCollapsed
    setHonorsCollapsed(next)
    localStorage.setItem('dc_honors_collapsed', String(next))
  }

  const fetchUserFaction = async () => {
    if (!navigator.onLine) return
    const { data } = await supabase.from('event_players').select('faction_id')
      .eq('user_id', user.id).order('joined_at', { ascending: false }).limit(1)
    setUserFactionId(data?.[0]?.faction_id || null)
  }

  const fetchPursuits = async () => {
    if (!navigator.onLine) return
    const data = await db.getMyPursuits(user.id)
    setPursuits(data || [])
  }

  const commitToBounty = async (bountyId) => {
    // Guard against duplicate pursuit
    if (pursuits.some(p => p.bounty_id === bountyId && p.user_id === user.id)) return

    await db.commitToBounty({ bountyId, userId: user.id, factionId: userFactionId })
    setPursuits(prev => [...prev, { bounty_id: bountyId, user_id: user.id, queued: true }])
    fetchPursuits().catch(() => {})
  }

  const standDown = async (bountyId) => {
    await db.standDown({ bountyId, userId: user.id })
    setPursuits(prev => prev.filter(p => p.bounty_id !== bountyId))
    fetchPursuits().catch(() => {})
  }

  // Gate the scanner on (1) event selection, (2) event status = live, and
  // (3) no active pause. Kills must never record with null event_id and
  // are meaningless during draft/prep/registration/locked/endex/closed or
  // a paused window.
  const openScannerWithEventGuard = async () => {
    setEventGateError(null)
    if (activePause) {
      setEventGateError('Game is paused. Scanning disabled until play resumes.')
      return
    }
    const stored = localStorage.getItem('dc_active_event')
    const events = await db.getActiveEventsForUser(user.id)
    const list = events || []

    // Bug 38: scanner is ALWAYS openable. With a live event the full QR
    // router runs (kill / intel / CP / equipment). Without one, only
    // ANUBIS-JOIN scans go anywhere — every other type is rejected inside
    // QRScanner's per-handler event gate. This is what lets a fresh
    // operator scan an event join QR before they have any registration.
    if (stored) {
      const ev = list.find(e => e.id === stored)
      if (!ev) {
        // Stored pointer is stale (event closed since last scan). Drop it
        // so the open scanner runs in out-of-event mode rather than
        // mistakenly attributing kills to a non-runtime event.
        localStorage.removeItem('dc_active_event')
        setActiveEventId(null)
      }
    } else if (list.length === 1) {
      // Convenience: single live registered event auto-selects.
      const id = list[0].id
      localStorage.setItem('dc_active_event', id)
      setActiveEventId(id)
      window.dispatchEvent(new CustomEvent('dc_event_selected', { detail: { eventId: id } }))
    }
    setShowScanner(true)
  }

  const subscribeToChanges = (eventId) => {
    // Filter realtime to ONLY the user's active event, not every bounty
    // anywhere in the system. Channel name carries eventId so re-subscribes
    // (when activeEventId changes) get a unique channel.
    //
    // bounties → refresh both bounty board AND fragos. A FRAGO is a bounty
    // with category='frago' so the FRAGO INSERT fires bounties INSERT, but
    // myFragos is filtered by node membership and lives in its own state —
    // the original handler only refetched bounties so the MISSIONS tab
    // stayed stale on a fresh delegation until the player navigated away.
    //
    // frago_assignments → refresh fragos. Delegating an existing FRAGO to
    // a new node inserts an assignments row without touching bounties,
    // so a bounties-only listener missed it entirely.
    const channel = supabase.channel(`player-${user.id}-${eventId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'bounties', filter: `event_id=eq.${eventId}` },
        async () => {
          if (!navigator.onLine) return
          // Bug 37: bust the SWR cache before refetch — otherwise the
          // bounties read returns the pre-realtime snapshot from IndexedDB
          // and the new/cancelled bounty stays invisible until hard refresh.
          await db.bustCache('bounties')
          fetchBounties(); fetchMyFragos()
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'frago_assignments' },
        () => { if (navigator.onLine) fetchMyFragos() }
      )
      // Track 1.3 — fill the spec'd realtime gaps for frago_phases /
      // frago_objectives. These tables are scoped via frago_id → bounties
      // (the event_id is one join away), so we DON'T filter on event_id
      // here. The fetchMyFragos refetch is a no-op for cross-event rows.
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'frago_phases' },
        () => {
          if (!navigator.onLine) return
          db.bustCache('bounties')
          fetchMyFragos()
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'frago_objectives' },
        () => {
          if (!navigator.onLine) return
          db.bustCache('bounties')
          fetchMyFragos()
        }
      )
      // team_members changes scoped to this user — covers a GM removing
      // them from a team via CommandCenter, or them being added to a new
      // team. Without this, the Teams tab stayed stale showing the old
      // membership until the next mount or online flip.
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'team_members', filter: `user_id=eq.${user.id}` },
        async () => {
          if (!navigator.onLine) return
          await db.bustCache('team_members')
          fetchMyTeams()
        }
      )
      .subscribe()
    // ANU-125: expire_bounties removed from client — pg_cron handles it server-side every minute.
    return () => { supabase.removeChannel(channel) }
  }

  const handleSignOut = async () => {
    // Track 1.1 — flush user state BEFORE auth.signOut() per the
    // hardening spec. Same belt-and-suspenders as Dashboard / Profile.
    await clearAllUserState()
    await supabase.auth.signOut()
    window.location.href = '/auth'
  }

  // ANU-86 — statsView defaults to 'event' whenever an event is selected;
  // 'career' only when there is no active event. Tap toggles for the
  // session; we don't persist the choice, so a fresh load (or joining a
  // new event) drops back to the event default — the at-game scorecard.
  const [statsView, setStatsView] = useState(() => activeEventId ? 'event' : 'career')
  useEffect(() => {
    setStatsView(activeEventId ? 'event' : 'career')
  }, [activeEventId])
  const displayStats = statsView === 'event' ? (eventStats || stats) : (stats || eventStats)
  const statsLabel = statsView === 'event' ? 'EVENT' : 'CAREER'

  // ANU-90 — IGA early-return. When the viewer's effective role at the
  // active event is 'iga' we swap the entire player surface for the
  // referee view (read-only both-faction SITREP + comms + war-crime ack
  // + BLIND MAN trigger). Hook runs unconditionally above the return so
  // hook order stays stable when the role changes mid-session.
  const { isIGA: viewerIsIGA } = useEffectiveRole(activeEventId)
  if (activeEventId && viewerIsIGA) {
    return <RefereeView eventId={activeEventId} />
  }

  return (
    <div style={S.page}>

      {/* ANU-91 — SAFETY-CRITICAL. Full-screen ceasefire overlay. z-9999,
          no dismiss, mounts BEFORE everything else so it lands over any
          modal, scanner, or banner. Scoped to the player's active event. */}
      {activeEventId && <BlindManOverlay eventId={activeEventId} />}

      {showBleedOut && <BleedOutTimer onDismiss={() => setShowBleedOut(false)} />}

      {/* Scanner overlay */}
      {showScanner && (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ background: '#14181a' }}>
          <div className="flex items-center justify-between px-4 py-3" style={S.nav}>
            <span style={{ ...S.displayFont, fontWeight: 700, fontSize: '14px' }}>Scan Target</span>
            <button onClick={() => setShowScanner(false)} style={S.muted}><X size={20} /></button>
          </div>
          <div className="flex-1 p-4">
            <QRScanner user={user} autoStart={!simulatedQrText} simulatedQrText={simulatedQrText} onResult={async () => {
              setShowScanner(false);
              setSimulatedQrText(null);
              await cache.clear('bounties').catch(() => {});
              fetchBounties();
              fetchStats();
              fetchEventStats();
            }} />
          </div>
        </div>
      )}

      <WhatsNew />

      {/* ── NAV ── */}
      <nav style={S.nav}>
        <div className="flex items-center gap-2">
          <img src="/anubis-logo.png" alt="Anubis" style={{ height: 28, width: 'auto', display: 'block' }} />
        </div>
        <div className="flex items-center gap-2 relative">
          <NotificationBell tone="recon" />
          <button
            onClick={() => setNavMenuOpen(o => !o)}
            className="flex items-center justify-center w-8 h-8"
            style={{ background: 'rgba(28, 38, 38, .8)', color: '#8aa090', border: '1px solid rgba(140, 180, 130, .2)' }}
            title="More"
            aria-label="More navigation"
          >
            <Menu size={16} />
          </button>
        </div>
      </nav>
      {/* Fix C: drawer rendered outside <nav> to escape backdropFilter stacking context */}
      {navMenuOpen && (
        <>
          <div
            onClick={() => setNavMenuOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.2)' }}
          />
          <div
            style={{
              position: 'fixed', top: 52, right: 12, zIndex: 10001,
              background: '#14181a', border: '1px solid rgba(140, 180, 130, .25)',
              padding: 10, minWidth: 180, display: 'flex', flexDirection: 'column', gap: 10,
              boxShadow: '0 8px 20px rgba(0,0,0,.4)',
            }}
          >
            <div className="flex items-center gap-2"><SyncIndicator /><span style={{ ...S.muted, fontSize: 11 }}>SYNC STATUS</span></div>
            <OfflineGuard><ViewSwitcher currentView="player" /></OfflineGuard>
            {isOrgAdmin && (
              <OfflineGuard>
                <button
                  onClick={() => { setNavMenuOpen(false); window.location.href = '/org' }}
                  className="flex items-center gap-2 px-2 py-2"
                  style={{ background: 'rgba(28, 38, 38, .8)', color: '#8aa090', border: '1px solid rgba(140, 180, 130, .2)' }}
                >
                  <Building2 size={16} /> <span style={{ fontSize: 12 }}>ORG ADMIN</span>
                </button>
              </OfflineGuard>
            )}
            <button
              onClick={() => { setNavMenuOpen(false); setShowQR(true) }}
              className="flex items-center gap-2 px-2 py-2"
              style={{ background: 'rgba(28, 38, 38, .8)', color: '#8aa090', border: '1px solid rgba(140, 180, 130, .2)' }}
            >
              <QrCodeIcon size={16} /> <span style={{ fontSize: 12 }}>MY QR CODE</span>
            </button>
            <button
              onClick={() => { setNavMenuOpen(false); window.location.href = '/profile' }}
              className="flex items-center gap-2 px-2 py-2"
              style={{ background: 'rgba(28, 38, 38, .8)', color: '#8aa090', border: '1px solid rgba(140, 180, 130, .2)' }}
            >
              <span style={{ fontSize: 12 }}>PROFILE</span>
            </button>
            <button
              onClick={() => { setNavMenuOpen(false); handleSignOut() }}
              className="flex items-center gap-2 px-2 py-2"
              style={{ background: 'rgba(255,65,54,.06)', color: '#ff4136', border: '1px solid rgba(255,65,54,.3)', cursor: 'pointer' }}
            >
              <LogOut size={16} /> <span style={{ fontSize: 12 }}>SIGN OUT</span>
            </button>
          </div>
        </>
      )}

      {/* ── GAME PAUSED BANNER ── */}
      {activePause && (
        <div className="px-4 py-3 text-center" style={{
          background: 'rgba(255,204,51,.18)',
          borderTop: '1px solid #ffcc33',
          borderBottom: '1px solid #ffcc33',
          color: '#ffcc33',
        }}>
          <p style={{
            fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif",
            fontSize: 14, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase',
          }}>
            ⏸ Game Paused{activePause.reason ? ` — ${activePause.reason}` : ''}
          </p>
          <p style={{ ...S.displayFont, fontSize: 9, color: 'rgba(255,204,51,.7)', marginTop: 2 }}>
            Scanning disabled until play resumes
          </p>
        </div>
      )}

      {/* ── ACTION BUTTONS ── */}
      <div className="px-4 pt-4 pb-3 flex gap-3">
        <button onClick={openScannerWithEventGuard} style={{ ...S.btnScan, opacity: activePause ? 0.4 : 1 }} disabled={!!activePause}>
          <span className="text-2xl">📡</span> Scan
        </button>
        <button onClick={() => setShowBleedOut(true)} style={S.btnDown}>
          <span className="text-2xl">💀</span> I'm Down
        </button>
      </div>

      {/* ── EVENT GATE ERROR ── */}
      {eventGateError && (
        <div className="px-4 py-3 flex items-center justify-between gap-2"
             style={{ background: 'rgba(255,106,61,.12)', borderTop: '1px solid rgba(255,106,61,.3)', borderBottom: '1px solid rgba(255,106,61,.3)' }}>
          <p className="text-xs" style={{ ...S.displayFont, color: '#ff6a3d', fontSize: '11px' }}>
            {eventGateError}
          </p>
          <button onClick={() => setEventGateError(null)}
                  style={{ color: '#ff6a3d', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}

      {/* ── PENDING HONORS ── */}
      {!isOnline ? (
        <div className="px-4 pt-3">
          <p className="text-xs italic text-center" style={S.faded}>Pending honors unavailable — offline</p>
        </div>
      ) : pendingConfirmations.length > 0 && (
        <div className="px-4 pt-3">
          <button onClick={toggleHonorsCollapsed} className="w-full flex items-center justify-between px-1 mb-2">
            <p className="text-xs" style={{ ...S.displayFont, ...S.muted, fontSize: '10px' }}>
              Pending Honors — {pendingConfirmations.length}
            </p>
            <span className="text-xs" style={S.faded}>{honorsCollapsed ? '▼ show' : '▲ hide'}</span>
          </button>
          {!honorsCollapsed && (
            <div className="space-y-2">
              {pendingConfirmations.map(kill => (
                <div key={kill.id} className="px-4 py-3 flex items-center justify-between gap-3" style={S.panel}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={S.text}>
                      {kill.killer?.callsign || kill.killer?.display_name || 'Unknown'} claimed a kill on you
                    </p>
                    <p className="text-xs mt-0.5" style={S.faded}>
                      {new Date(kill.scanned_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => confirmKill(kill.id)}
                      className="text-xs px-3 py-2" style={S.btnPrimary}>Honor</button>
                    <button onClick={() => dismissKill(kill.id)}
                      className="text-xs px-3 py-2" style={S.btnSecondary}>Dishonor</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── QR MODAL ── */}
      {showQR && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowQR(false)}>
          <div className="p-6 flex flex-col items-center gap-4 max-w-xs w-full"
            style={{ ...S.panel, background: 'rgba(28, 38, 38, .95)' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ ...S.displayFont, fontSize: '18px', fontWeight: 700 }}>{profile?.display_name}</h2>
            {/* Track 3.4 — generate the bare-UUID kill QR (canonical
                format). qrRouter still parses the legacy deathcard://
                form for old patches; new patches printed from here
                encode the bare UUID and skip the prefix entirely. */}
            <div className="bg-white p-2 rounded-xl"><QRCode id="player-id-qr" value={user.id} size={200} /></div>
            <p className="text-xs text-center" style={S.faded}>Show this card to be scanned</p>
            <div className="flex gap-3">
              <button onClick={() => downloadQRPng({ svgElementId: 'player-id-qr', filename: `dc-card-${(profile?.callsign || profile?.display_name || 'me').replace(/\s+/g, '-')}` })}
                className="text-sm px-3 py-1.5"
                style={{ background: '#9adb6e', color: '#14181a', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', border: 'none', cursor: 'pointer' }}>
                Save PNG
              </button>
              <button onClick={() => setShowQR(false)} className="text-sm" style={S.muted}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── BOUNTIES ON ME ALERT ── */}
      {myBounties.length > 0 && (
        <div className="px-4 py-2" style={{
          background: 'rgba(255, 106, 61, 0.08)',
          borderBottom: '1px solid rgba(255, 106, 61, .3)',
        }}>
          <p className="text-sm font-medium flex items-center gap-2" style={{
            ...S.displayFont, ...S.warn, fontSize: '12px'
          }}>
            <Target size={14} />
            ⚠ {myBounties.length} active {myBounties.length === 1 ? 'bounty' : 'bounties'} on you
          </p>
        </div>
      )}

      {/* ── STATS BAR ── tap to toggle EVENT ↔ CAREER */}
      {(stats || eventStats) ? (
        <button
          type="button"
          onClick={() => setStatsView(statsView === 'event' ? 'career' : 'event')}
          className="w-full px-3 py-2 text-left"
          style={{
            ...S.panel,
            borderTop: 'none',
            borderLeft: 'none',
            borderRight: 'none',
            borderBottom: '1px solid rgba(140, 180, 130, .2)',
            borderRadius: 0,
            background: 'transparent',
            cursor: 'pointer',
          }}
          aria-label={`Showing ${statsLabel} stats — tap to toggle`}
        >
          <p className="text-center mb-1" style={{ ...S.displayFont, fontSize: '9px', color: '#5a6a60', letterSpacing: '0.14em' }}>
            {statsView === 'event' ? '◆ EVENT STATS' : '◇ CAREER STATS'} <span style={{ opacity: 0.6 }}>· tap to switch</span>
          </p>
          {/* Tile count = (5 or 6) base + (1 if pending kills surface).
              Tailwind needs literal class names so the dynamic value is
              mapped to a fixed lookup; safe for the JIT scanner. */}
          <div className={`grid gap-1 ${(() => {
            const n = (hideFactionScores ? 5 : 6) + (myPendingKills > 0 ? 1 : 0)
            return { 5: 'grid-cols-5', 6: 'grid-cols-6', 7: 'grid-cols-7' }[n] || 'grid-cols-6'
          })()}`}>
            {[
              // Pts tile dropped when org has fog-of-war on; the rest of the
              // bar (kills/deaths/confirmed/etc) stays so players still see
              // their own action history.
              hideFactionScores ? null : { val: displayStats?.total_points ?? 0, label: 'Pts', color: '#9adb6e' },
              // Kills tile is the SUM of confirmed + unconfirmed — a kill sits
              // in exactly one bucket (never both, never doubled).
              { val: (displayStats?.career_confirmed_kills ?? 0) + (displayStats?.career_unconfirmed_kills ?? 0), label: 'Kills', color: '#e0e8de' },
              { val: displayStats?.total_deaths ?? 0, label: 'Deaths', color: '#e0e8de' },
              // TD-17: kills I submitted that are still pending the target's
              // honor/dishonor — orange to signal "action expected from
              // someone else, not me". Hidden when zero so the tile doesn't
              // squat on the scorecard for inactive players.
              myPendingKills > 0 ? { val: myPendingKills, label: 'Pending', color: '#ffcc33' } : null,
              // Career view shows KD ratio (one decimal) where event view
              // shows confirmed kill count — the views diverge here because
              // KD is meaningful across many events but noisy per-event.
              statsView === 'career'
                ? { val: (displayStats?.kd_ratio != null ? Number(displayStats.kd_ratio).toFixed(1) : '—'), label: 'KD', color: '#4dd6c2' }
                : { val: displayStats?.career_confirmed_kills ?? 0, label: 'Confirmed', color: '#4dd6c2' },
              { val: displayStats?.bounties_claimed ?? 0, label: 'Claimed', color: '#9adb6e' },
              statsView === 'career'
                ? { val: displayStats?.events_played ?? 0, label: 'Events', color: '#e0e8de' }
                : { val: displayStats?.bounties_survived ?? 0, label: 'Survived', color: '#e0e8de' },
            ].filter(Boolean).map((s, i) => (
              <div key={i} className="text-center py-1">
                <p className="font-bold text-base leading-tight" style={{
                  fontFamily: "'Rajdhani', sans-serif", color: s.color, fontVariantNumeric: 'tabular-nums',
                }}>{s.val}</p>
                <p style={{ color: '#5a6a60', fontSize: '9px', letterSpacing: '0.14em', textTransform: 'uppercase' }}>{s.label}</p>
              </div>
            ))}
          </div>
        </button>
      ) : !isOnline ? (
        <div className="px-4 py-2 text-center" style={{
          ...S.panel, borderRadius: 0, borderLeft: 'none', borderRight: 'none', borderTop: 'none',
        }}>
          <p className="text-xs italic" style={S.faded}>Stats unavailable — offline</p>
        </div>
      ) : null}

      {/* ── IDENTITY CARD ──
          Renders only when active event selected AND player is on a node.
          Hidden silently if the player has no node membership in this event. */}
      {activeEventId && <IdentityCard user={user} activeEventId={activeEventId} />}

      {/* ── BOUNTY LIST ── */}
      <main className="max-w-lg mx-auto p-3">
        <div className="flex gap-2 mb-4">
          {/* Teams is feature-gated for September. M55 staff still see it
              so they can debug; everyone else gets ops / missions / events. */}
          {(isPlatformStaff(profile) ? ['ops', 'missions', 'events', 'teams'] : ['ops', 'missions', 'events']).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className="flex-1 font-medium text-xs transition-colors"
              style={{
                ...(activeTab === tab ? {
                  background: '#9adb6e', color: '#14181a',
                  boxShadow: '0 0 8px rgba(154, 219, 110, .5)',
                } : {
                  background: 'rgba(28, 38, 38, .6)', color: '#8aa090',
                  border: '1px solid rgba(140, 180, 130, .2)',
                }),
                // Track 6.2 — 44x44 min hit area. py-2.5 ≈ 10px top/bottom
                // on a 12px font landed at ~32px tall, under the WCAG spec.
                minHeight: '44px', padding: '10px 8px',
                fontFamily: "'Rajdhani', sans-serif", letterSpacing: '0.14em', textTransform: 'uppercase',
              }}>
              {tab === 'ops' ? `Bounties (${activeBounties.length})`
                : tab === 'missions' ? `Missions (${myFragos.filter(f => f.status === 'active').length})`
                : tab === 'events' ? 'Events'
                : `Teams (${myTeams.length})`}
            </button>
          ))}
        </div>

        {activeTab === 'ops' && (
          <div className="space-y-3">

            {/* ANU-103 — VIP PROTECT BOUNTY CARD */}
            {myProtectBounty && (() => {
              const activeRegs = protectRegistrations.filter(r => !r.abandoned_at)
              const hasTeam = activeRegs.length >= (myProtectBounty.min_protectors || 1)
              return (
                <div style={{ background: 'rgba(77,160,255,.06)', border: '1px solid rgba(77,160,255,.4)', padding: 16, position: 'relative' }}>
                  <p style={{ ...S.displayFont, fontSize: 13, color: '#4da0ff', letterSpacing: '0.12em', marginBottom: 6 }}>
                    🛡 PROTECT BOUNTY — {myProtectBounty.status === 'active' ? 'ACTIVE' : 'RECRUITING'}
                  </p>
                  <p style={{ ...S.mono, fontSize: 11, color: '#8aa090', marginBottom: 8 }}>
                    Mode: {(myProtectBounty.protect_mode || 'survive').toUpperCase()} · {myProtectBounty.min_protectors || 1} protector(s) required
                  </p>
                  {protectRegistrations.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      {protectRegistrations.map(r => (
                        <p key={r.id} style={{ ...S.mono, fontSize: 10, color: r.abandoned_at ? '#5a6a60' : '#9adb6e' }}>
                          {r.abandoned_at ? '✗' : '✓'} {r.protector?.callsign || r.protector?.display_name || 'Unknown'}
                          {r.abandoned_at ? ' (abandoned)' : ''}
                        </p>
                      ))}
                    </div>
                  )}
                  {myProtectBounty.status === 'registration' && (
                    <button
                      onClick={async () => {
                        if (!hasTeam || teamInPlaceBusy) return
                        setTeamInPlaceBusy(true)
                        await supabase.from('bounties').update({ status: 'active', activated_at: new Date().toISOString() }).eq('id', myProtectBounty.id)
                        await fetchProtectData()
                        setTeamInPlaceBusy(false)
                      }}
                      disabled={!hasTeam || teamInPlaceBusy}
                      style={{ width: '100%', padding: '10px', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 14, letterSpacing: '0.12em', textTransform: 'uppercase', background: hasTeam ? 'rgba(77,160,255,.15)' : 'rgba(0,20,8,.4)', color: hasTeam ? '#4da0ff' : '#4aaa5a', border: `1px solid ${hasTeam ? 'rgba(77,160,255,.4)' : 'rgba(0,255,102,.18)'}`, cursor: hasTeam ? 'pointer' : 'not-allowed' }}>
                      {teamInPlaceBusy ? 'ACTIVATING...' : hasTeam ? '✓ TEAM IN PLACE — ACTIVATE PROTECTION' : `WAITING FOR TEAM (${activeRegs.length}/${myProtectBounty.min_protectors || 1})`}
                    </button>
                  )}
                  {myProtectBounty.status === 'active' && (
                    <>
                      {!deadConfirm ? (
                        <button
                          onClick={() => { setDeadConfirm(true); setDeadCountdown(30) }}
                          style={{ width: '100%', padding: '10px', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 14, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'rgba(255,42,42,.12)', color: '#ff4444', border: '1px solid rgba(255,42,42,.4)', cursor: 'pointer', marginTop: 8 }}>
                          ☠ I'M DEAD — REPORT ELIMINATION
                        </button>
                      ) : (
                        <div style={{ background: 'rgba(255,42,42,.12)', border: '1px solid rgba(255,42,42,.4)', padding: 12, marginTop: 8 }}>
                          <p style={{ ...S.mono, fontSize: 11, color: '#ff4444', marginBottom: 8 }}>
                            Confirm elimination? This will fail the protection bounty. ({deadCountdown}s)
                          </p>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={async () => {
                              setDeadBusy(true)
                              await supabase.from('bounties').update({ status: 'failed' }).eq('id', myProtectBounty.id)
                              setDeadConfirm(false)
                              setDeadBusy(false)
                              await fetchProtectData()
                            }} disabled={deadBusy} style={{ flex: 1, padding: '8px', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 13, background: '#ff2a2a', color: '#fff', border: 'none', cursor: 'pointer' }}>
                              {deadBusy ? '...' : 'CONFIRM ELIMINATED'}
                            </button>
                            <button onClick={() => setDeadConfirm(false)} style={{ flex: 1, padding: '8px', fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 13, background: 'rgba(0,20,8,.4)', color: '#8aa090', border: '1px solid rgba(140,180,130,.2)', cursor: 'pointer' }}>CANCEL</button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )
            })()}

            {/* ANU-103 — PROTECTOR CARD */}
            {myProtectorReg && !myProtectBounty && (() => {
              const b = myProtectorReg.bounty
              const vipName = b?.vip?.callsign || b?.vip?.display_name || 'VIP'
              return (
                <div style={{ background: 'rgba(154,219,110,.04)', border: '1px solid rgba(154,219,110,.3)', padding: 16 }}>
                  <p style={{ ...S.displayFont, fontSize: 13, color: '#9adb6e', letterSpacing: '0.12em', marginBottom: 4 }}>
                    🛡 ON PROTECTION DETAIL
                  </p>
                  <p style={{ ...S.mono, fontSize: 11, color: '#8aa090', marginBottom: 8 }}>
                    VIP: {vipName} · Mode: {(b?.protect_mode || 'survive').toUpperCase()} · Status: {(b?.status || '').toUpperCase()}
                  </p>
                  <button
                    onClick={async () => {
                      if (abandonBusy) return
                      if (!window.confirm(`Abandon protection detail for ${vipName}? This is permanent for this bounty.`)) return
                      setAbandonBusy(true)
                      await supabase.from('protect_registrations').update({ abandoned_at: new Date().toISOString() }).eq('id', myProtectorReg.id)
                      setAbandonBusy(false)
                      await fetchProtectData()
                    }}
                    disabled={abandonBusy}
                    style={{ padding: '8px 16px', fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'rgba(255,106,61,.08)', color: '#ff6a3d', border: '1px solid rgba(255,106,61,.3)', cursor: 'pointer' }}>
                    {abandonBusy ? 'ABANDONING...' : 'ABANDON DETAIL'}
                  </button>
                </div>
              )
            })()}

            <DataState
              loading={bountiesLoading && activeBounties.length === 0}
              error={bountiesError}
              empty={activeBounties.length === 0}
              tone="recon"
              loadingMessage="Loading bounties…"
              emptyMessage="No active bounties"
              emptyHint="When a GM dispatches one, it'll show up here."
              onRetry={fetchBounties}
            >
            {activeBounties.map(bounty => {
                const edge = typeEdge[bounty.bounty_type] || typeEdge.kill
                const tagS = typeTagStyle[bounty.bounty_type] || typeTagStyle.kill
                const bountyPursuits = pursuits.filter(p => p.bounty_id === bounty.id)
                const myPursuit = bountyPursuits.find(p => p.user_id === user.id)

                return (
                  <div key={bounty.id} className="p-4 relative overflow-hidden" 
                    style={{ ...S.panel, cursor: navigator.onLine ? 'pointer' : 'default' }}
                    onClick={() => { if (navigator.onLine) window.location.href = `/bounty/${bounty.id}` }}>
                    {/* Type edge */}
                    <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: edge }} />
                    {/* Bracket corners */}
                    <div className="absolute top-0 left-0 w-3 h-3" style={{ borderTop: '1.5px solid #9adb6e', borderLeft: '1.5px solid #9adb6e' }} />
                    <div className="absolute bottom-0 right-0 w-3 h-3" style={{ borderBottom: '1.5px solid #9adb6e', borderRight: '1.5px solid #9adb6e' }} />

                    <div className="flex justify-between items-start gap-2 ml-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-base truncate" style={{
                          ...S.displayFont, fontSize: '15px', fontWeight: 700, color: '#e0e8de',
                        }}>
                          {bounty.target?.callsign || bounty.target?.display_name || 'Unknown'}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span style={{ ...S.tag, ...tagS }}>{bounty.bounty_type}</span>
                          <span className="text-xs" style={S.faded}>by {bounty.issuer?.display_name}</span>
                        </div>
                        {bounty.notes && (
                          <p className="text-xs italic mt-1" style={S.faded}>"{bounty.notes}"</p>
                        )}
                        {bounty.expires_at && (
                          <p className="text-xs mt-1" style={S.warn}>
                            <CountdownTimer expiresAt={bounty.expires_at} />
                          </p>
                        )}
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          {myPursuit ? (
                            <button onClick={(e) => {
                              e.stopPropagation() 
                              standDown(bounty.id)
                            }}
                              className="flex items-center gap-1 text-xs px-2 py-1"
                              style={{ background: 'rgba(255, 106, 61, .12)', color: '#ff6a3d', border: '1px solid rgba(255,106,61,.3)', ...S.displayFont, fontSize: '10px' }}>
                              <X size={11} /> Stand down
                            </button>
                          ) : (
                            <button onClick={(e) => {
                              e.stopPropagation()
                              commitToBounty(bounty.id)
                            }}
                              className="flex items-center gap-1 text-xs px-2 py-1"
                              style={{ background: 'rgba(77, 214, 194, .08)', color: '#4dd6c2', border: '1px solid rgba(77,214,194,.3)', ...S.displayFont, fontSize: '10px' }}>
                              <Crosshair size={11} /> Pursue
                            </button>
                          )}
                          {bountyPursuits.length > 0 && (
                            <span className="text-xs flex items-center gap-1" style={S.cyan}>
                              <Crosshair size={10} /> {bountyPursuits.length} pursuing
                            </span>
                          )}
                        </div>
                        {!navigator.onLine && (
                          <span style={{ fontSize: '9px', color: '#ff6a3d', letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Rajdhani', sans-serif", opacity: 0.7, marginTop: '4px', display: 'block' }}>
                            DOSSIER OFFLINE
                          </span>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        {/* Fog-of-war: bounty card keeps the "BOUNTY" badge but
                            drops the point number when the org has scores hidden. */}
                        {hideFactionScores ? (
                          <span style={{ ...S.displayFont, fontSize: '11px', color: '#9adb6e', letterSpacing: '0.14em' }}>BOUNTY</span>
                        ) : (
                          <>
                            <span className="font-bold text-xl" style={{
                              fontFamily: "'Rajdhani', sans-serif", color: '#9adb6e', fontVariantNumeric: 'tabular-nums',
                            }}>{bounty.points}</span>
                            <p style={{ color: '#5a6a60', fontSize: '8px', letterSpacing: '0.14em', textTransform: 'uppercase', textAlign: 'right' }}>PTS</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </DataState>
          </div>
        )}

        {activeTab === 'missions' && (
          <div className="space-y-3">
            <DataState
              loading={fragosLoading && myFragos.length === 0}
              error={fragosError}
              empty={myFragos.length === 0}
              tone="recon"
              loadingMessage="Loading missions…"
              emptyMessage="No missions assigned"
              emptyHint="FRAGOs will appear here when issued to your faction or node."
              onRetry={fetchMyFragos}
            >
            {(() => {
              // FIX 5 / FIX 10 — split active missions from
              // completed/failed. Single isResolved helper so a FRAGO
              // doesn't show up in BOTH lists (a failed FRAGO with a
              // still-pursuing assignment row used to slip back into
              // the active bucket via the pursuit branch). Resolved
              // wins outright.
              const isResolved = f =>
                ['claimed','failed','expired','cancelled'].includes(f.status) ||
                f.phase === 'resolved'
              const activeMissions = myFragos.filter(f =>
                !isResolved(f) && (
                  f.status === 'active' ||
                  (f.frago_assignments || []).some(a => a.status === 'pursuing')
                )
              )
              const resolvedMissions = myFragos.filter(isResolved)
              const renderMissionCard = (frago) => {
                const targetFactionBroadcast = frago.target_type === 'faction'
                const expiresAt = frago.expires_at ? new Date(frago.expires_at) : null
                const isResolved = frago.status === 'claimed' || frago.phase === 'resolved'
                const isFailed = frago.status === 'failed'
                const isActive = frago.status === 'active'
                const statusColor = isActive ? '#ffcc33' : isFailed ? '#ff6a3d' : isResolved ? '#9adb6e' : '#8aa090'
                // SEC 2 — assignment buckets by the new lifecycle.
                //   pending = freshly delegated, awaiting accept/decline
                //   active  = legacy ("just assigned"), treat like pending
                //   accepted = recipient owns it; can delegate down or
                //              abort if mid-mission
                const myPendingAssignments  = (frago.myAssignments || []).filter(a => a.status === 'pending' || a.status === 'active')
                const myAcceptedAssignments = (frago.myAssignments || []).filter(a => a.status === 'accepted')
                // Per operator correction (2026-05-16): accept is a
                // commitment. DELEGATE is offered only on pending —
                // if the leader needs to pass it to a specific child
                // after committing, they ABORT and the parent
                // re-delegates.
                const myDelegateableAssignment = myPendingAssignments[0] || null
                const ledNodeIdsForThis = (frago.myAssignments || []).map(a => a.node_id)
                const childNodesForDelegate = myChildNodes.filter(c => ledNodeIdsForThis.includes(c.parent_id))
                const showAccept = isActive && myPendingAssignments.length > 0
                const showDecline = isActive && myPendingAssignments.length > 0
                const showAbort = isActive && myAcceptedAssignments.length > 0
                const showDelegate = isActive && childNodesForDelegate.length > 0 && !!myDelegateableAssignment
                // Broadcast pursue: only on broadcast FRAGOs, only when the
                // player hasn't already pursued. Stand-down available on the
                // pursuing row this user owns.
                const myPursuit = (frago.myAssignments || []).find(a => a.status === 'pursuing')
                // PURSUE was the broadcast opt-in path. With the
                // routing simplification every new FRAGO lands at
                // least one chain assignment at activation, so PURSUE
                // is meaningless unless the FRAGO is a pre-routing
                // legacy fixture with zero assignments. STAND DOWN
                // stays available for any existing pursuit rows so
                // the player can still back out.
                const hasAnyAssignment = (frago.frago_assignments || []).length > 0
                const showPursue = targetFactionBroadcast && isActive && !myPursuit && !hasAnyAssignment
                const showStandDown = targetFactionBroadcast && isActive && !!myPursuit

                return (
                  <div key={frago.id} className="p-4 relative" style={{
                    background: 'rgba(255, 204, 51, .06)',
                    border: `1px solid ${isActive ? 'rgba(255, 204, 51, .4)' : 'rgba(255, 204, 51, .15)'}`,
                    boxShadow: isActive ? '0 0 8px rgba(255, 204, 51, .12)' : 'none',
                    opacity: isResolved ? 0.65 : 1,
                    cursor: navigator.onLine ? 'pointer' : 'default',
                  }}
                  onClick={() => { if (navigator.onLine) window.location.href = `/frago/${frago.id}` }}>
                    {/* Amber corner brackets to differentiate from green bounty cards */}
                    <div style={{ position: 'absolute', top: 0, left: 0, width: 8, height: 8, borderTop: `1.5px solid ${statusColor}`, borderLeft: `1.5px solid ${statusColor}` }} />
                    <div style={{ position: 'absolute', bottom: 0, right: 0, width: 8, height: 8, borderBottom: `1.5px solid ${statusColor}`, borderRight: `1.5px solid ${statusColor}` }} />
                    {/* Left edge accent */}
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 2, background: statusColor }} />

                    <div className="flex justify-between items-start gap-2 ml-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span style={{
                            ...S.tag, ...S.displayFont,
                            color: '#ffcc33', borderColor: 'rgba(255, 204, 51, .4)', background: 'rgba(255, 204, 51, .08)',
                          }}>
                            FRAGO
                          </span>
                          <span style={{
                            ...S.tag, ...S.displayFont,
                            color: statusColor, borderColor: statusColor + '66', background: statusColor + '14',
                          }}>
                            ● {isResolved ? 'COMPLETE' : frago.status.toUpperCase()}
                          </span>
                          {targetFactionBroadcast && (frago.frago_assignments || []).length === 0 && (
                            <span style={{ ...S.tag, ...S.displayFont, color: '#8aa090', borderColor: 'rgba(140,160,144,.3)', background: 'rgba(140,160,144,.08)' }}>
                              📡 BROADCAST
                            </span>
                          )}
                          {/* FIX 9 — DELEGATED TO label so the card
                              reads as a tasking, not a broadcast. Uses
                              the first assignment's node callsign /
                              name; multi-node delegation shows "+N". */}
                          {(frago.frago_assignments || []).length > 0 && (() => {
                            const assigns = frago.frago_assignments
                            const primary = assigns[0]?.node
                            const label = primary?.callsign || primary?.name || 'NODE'
                            const extras = assigns.length - 1
                            return (
                              <span style={{ ...S.tag, ...S.displayFont, color: '#4dd6c2', borderColor: 'rgba(77,214,194,.4)', background: 'rgba(77,214,194,.10)' }}>
                                ◈ DELEGATED TO {label.toUpperCase()}{extras > 0 ? ` +${extras}` : ''}
                              </span>
                            )
                          })()}
                        </div>
                        <p className="font-bold text-base" style={{ ...S.displayFont, fontSize: '15px', fontWeight: 700, color: '#ffcc33' }}>
                          {frago.mission_name || '(unnamed mission)'}
                        </p>
                        {/* FRAGO restructure — dedicated frago_description
                            is the in-character mission statement. Falls
                            back to notes for pre-restructure FRAGOs that
                            stuffed the description into the activation
                            message via notes. */}
                        {(frago.frago_description || frago.notes) && (
                          <p className="text-xs mt-2" style={{ color: '#e0e8de', lineHeight: 1.5, whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
                            {frago.frago_description || frago.notes}
                          </p>
                        )}
                        {/* SEC 4 — scenario role banner. When the
                            FRAGO assigns the current player a role,
                            surface it as a prominent banner so they
                            read in-character ("YOU ARE: The Pilot"). */}
                        {(() => {
                          const myRole = (frago.scenario_roles || []).find(sr => sr.user_id === user.id)
                          if (!myRole) return null
                          return (
                            <div className="mt-2 p-2" style={{
                              background: 'rgba(192,138,42,.12)',
                              border: '1px solid rgba(192,138,42,.5)',
                              boxShadow: '0 0 8px rgba(192,138,42,.18)',
                            }}>
                              <p style={{
                                ...S.displayFont, fontSize: '11px',
                                color: '#c08a2a', letterSpacing: '0.14em',
                              }}>
                                ◆ YOU ARE: {myRole.role_name?.toUpperCase()}
                              </p>
                              {myRole.description && (
                                <p style={{ color: '#e0d4a0', fontSize: 12, marginTop: 4, lineHeight: 1.4, fontStyle: 'italic' }}>
                                  {myRole.description}
                                </p>
                              )}
                            </div>
                          )
                        })()}
                        {/* Phase 5 of the FRAGO Rework — structured phase
                            checklist so the player can see exactly which
                            objective is "do this now". Empty stepper means
                            this is a legacy FRAGO without phases.
                            FRAGO Fix 2 — viewerScope="player" hides
                            locked phases + objectives so the player
                            only sees the current step + completed
                            history. Surprise is the gameplay. */}
                        <div className="mt-3">
                          <FragoPhaseStepper fragoId={frago.id} viewerScope="player" />
                        </div>
                        {expiresAt && isActive && (
                          <p className="text-xs mt-2" style={S.warn}>
                            <CountdownTimer expiresAt={frago.expires_at} />
                          </p>
                        )}

                        {/* Assignment status if any (delegate/multi) */}
                        {!targetFactionBroadcast && (frago.myAssignments || []).length > 0 && (
                          <div className="mt-2 pt-2" style={{ borderTop: '1px solid rgba(255,204,51,.15)' }}>
                            {(frago.myAssignments || []).map(a => (
                              <p key={a.id} className="text-xs" style={S.muted}>
                                {a.node?.callsign ? `${a.node.callsign} — ` : ''}{a.node?.name || 'node'}
                                <span style={{ marginLeft: 8, color: a.status === 'accepted' ? '#9adb6e' : a.status === 'completed' ? '#9adb6e' : a.status === 'failed' ? '#ff6a3d' : '#ffcc33' }}>
                                  · {a.status}
                                </span>
                              </p>
                            ))}
                          </div>
                        )}

                        {/* Broadcast pursue / stand-down */}
                        {(showPursue || showStandDown) && (
                          <div className="flex gap-2 mt-3 flex-wrap">
                            {showPursue && (
                              <button
                                onClick={(e) => { e.stopPropagation(); pursueFrago(frago.id) }}
                                style={{
                                  ...S.displayFont, fontSize: '11px',
                                  background: 'rgba(77, 214, 194, .12)', color: '#4dd6c2',
                                  border: '1px solid rgba(77,214,194,.4)',
                                  padding: '6px 12px', cursor: 'pointer',
                                  fontWeight: 700, letterSpacing: '0.12em',
                                }}>
                                <Crosshair size={11} /> PURSUE
                              </button>
                            )}
                            {showStandDown && (
                              <button
                                onClick={(e) => { e.stopPropagation(); standDownFrago(myPursuit.id) }}
                                style={{
                                  ...S.displayFont, fontSize: '11px',
                                  background: 'rgba(255, 106, 61, .12)', color: '#ff6a3d',
                                  border: '1px solid rgba(255,106,61,.4)',
                                  padding: '6px 12px', cursor: 'pointer',
                                  fontWeight: 700, letterSpacing: '0.12em',
                                }}>
                                <X size={11} /> STAND DOWN
                              </button>
                            )}
                          </div>
                        )}

                        {/* Leader actions */}
                        {(showAccept || showDelegate || showDecline || showAbort) && (
                          <div className="flex gap-2 mt-3 flex-wrap">
                            {showAccept && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  myPendingAssignments.forEach(a => acceptFragoAssignment(a.id))
                                }}
                                style={{
                                  ...S.displayFont, fontSize: '11px',
                                  background: '#9adb6e', color: '#14181a',
                                  border: 'none', padding: '6px 12px', cursor: 'pointer',
                                  fontWeight: 700, letterSpacing: '0.12em',
                                }}>
                                ✓ ACCEPT
                              </button>
                            )}
                            {showDelegate && (
                              <select
                                onClick={(e) => e.stopPropagation()}
                                onChange={e => { e.stopPropagation(); delegateFragoToChild(myDelegateableAssignment?.id, e.target.value); e.target.value = '' }}
                                value=""
                                style={{
                                  ...S.displayFont, fontSize: '11px',
                                  background: 'rgba(28,38,38,.6)', color: '#ffcc33',
                                  border: '1px solid rgba(255,204,51,.4)',
                                  padding: '6px 10px', cursor: 'pointer',
                                }}>
                                <option value="">⊳ DELEGATE TO...</option>
                                {childNodesForDelegate.map(c => (
                                  <option key={c.id} value={c.id} style={{ background: '#14181a', color: '#e0e8de' }}>
                                    {c.callsign ? `${c.callsign} — ${c.name}` : c.name}
                                  </option>
                                ))}
                              </select>
                            )}
                            {/* SEC 2 — DECLINE on a pending assignment
                                returns it up one level for re-delegation. */}
                            {showDecline && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  myPendingAssignments.forEach(a => declineFragoAssignment(a.id))
                                }}
                                style={{
                                  ...S.displayFont, fontSize: '11px',
                                  background: 'rgba(140,160,144,.12)', color: '#8aa090',
                                  border: '1px solid rgba(140,160,144,.4)',
                                  padding: '6px 12px', cursor: 'pointer',
                                  letterSpacing: '0.12em',
                                }}>
                                ⤺ DECLINE
                              </button>
                            )}
                            {/* SEC 2 — ABORT MISSION on an already-
                                accepted assignment. Returns the FRAGO
                                up the chain to whoever delegated it
                                without failing the mission; completed
                                objectives are preserved. */}
                            {showAbort && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  myAcceptedAssignments.forEach(a => abortFragoAssignment(a.id))
                                }}
                                style={{
                                  ...S.displayFont, fontSize: '11px',
                                  background: 'rgba(255,42,42,.12)', color: '#ff4136',
                                  border: '1px solid rgba(255,42,42,.4)',
                                  padding: '6px 12px', cursor: 'pointer',
                                  letterSpacing: '0.12em', fontWeight: 700,
                                }}>
                                ✕ ABORT MISSION
                              </button>
                            )}
                            {myAcceptedAssignments.length > 0 && (
                              <span style={{ ...S.displayFont, fontSize: '10px', color: '#9adb6e', alignSelf: 'center' }}>
                                ✓ ACCEPTED
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        {hideFactionScores ? (
                          <span style={{ ...S.displayFont, fontSize: '11px', color: '#ffcc33', letterSpacing: '0.14em' }}>FRAGO</span>
                        ) : (
                          <>
                            <span className="font-bold text-xl" style={{ fontFamily: "'Rajdhani', sans-serif", color: '#ffcc33', fontVariantNumeric: 'tabular-nums' }}>
                              {frago.points}
                            </span>
                            <p style={{ color: '#5a6a60', fontSize: '8px', letterSpacing: '0.14em', textTransform: 'uppercase', textAlign: 'right' }}>PTS</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )
              }
              return (
                <>
                  {activeMissions.map(renderMissionCard)}
                  {resolvedMissions.length > 0 && (
                    <div className="mt-4">
                      <button onClick={toggleResolvedMissions}
                        className="w-full flex items-center justify-between"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 8 }}>
                        <span style={{
                          fontFamily: "'Rajdhani', sans-serif", fontSize: 10, fontWeight: 700,
                          letterSpacing: '0.14em', textTransform: 'uppercase',
                          color: '#5a6a60',
                        }}>
                          Completed / Failed — {resolvedMissions.length}
                        </span>
                        <span style={{ ...S.faded, fontSize: 10 }}>{resolvedMissionsCollapsed ? '▼ show' : '▲ hide'}</span>
                      </button>
                      {!resolvedMissionsCollapsed && (
                        <div className="space-y-2">
                          {resolvedMissions.map(renderMissionCard)}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )
            })()}
            </DataState>
          </div>
        )}

        {activeTab === 'events' && (
          <EventsTab userId={user.id} />
        )}

        {activeTab === 'teams' && (
          <div className="space-y-2">
            <button
              onClick={() => window.location.href = '/teams?create=true'}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-2 text-sm"
              style={S.btnPrimary}>
              <Plus size={14} /> New Team
            </button>
            <DataState
              loading={teamsLoading && myTeams.length === 0}
              error={teamsError}
              empty={myTeams.length === 0}
              tone="recon"
              loadingMessage="Loading teams…"
              emptyMessage="No teams"
              emptyHint="Create one above or join via an invite link."
              onRetry={fetchMyTeams}
            >
              {myTeams.map((member, i) => (
                <div key={i}
                  onClick={() => window.location.href = `/teams/${member.teams?.id}?from=player`}
                  className="px-4 py-3 cursor-pointer" style={S.panel}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate" style={S.text}>{member.teams?.name || 'Unknown Team'}</p>
                      {member.field_role && <p className="text-xs mt-0.5" style={S.muted}>{member.field_role}</p>}
                    </div>
                    <span className="text-xs flex-shrink-0 capitalize" style={S.faded}>{member.team_role}</span>
                  </div>
                </div>
              ))}
            </DataState>
          </div>
        )}


        {/* ANU-89 — war crime report. Lives at the bottom of the player
            surface so it's reachable but doesn't compete with the bounty
            board / scan flow visually. The button is gated to live events
            internally, so this slot is a no-op outside live play. */}
        {activeEventId && (
          <div style={{ padding: '14px 16px 24px' }}>
            <WarCrimeReportButton
              eventId={activeEventId}
              eventStatus={activeEventStatus}
              factionId={eventPlayerFactions[activeEventId]}
            />
          </div>
        )}

      </main>

      {/* Dev-only scan helper — bypasses the camera. */}
      <DevScanPanel
        activeEventId={activeEventId}
        profile={profile}
        onSimulate={(text) => { setSimulatedQrText(text); setShowScanner(true) }}
      />
    </div>
  )
}
